import { useLazyQuery } from "@apollo/client";
import { format } from "date-fns";
import * as FileSaver from "file-saver";
import gql from "graphql-tag";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { formatExcelData } from "../../../../utils/formatExcelData";
import { Icon } from "../../../Icon/Icon";
import { Button } from "../../../SharedComponents/Button/Button";
import "./AccountancyReport.css";

export const AccountancyReport = ({
  fromDate,
  endDate,
  name,
  distributorId,
  usage,
  accounts,
}) => {
  const { t } = useTranslation();
  const formattedFrom = format(new Date(fromDate), "yyyy-MM");
  const formattedTo = format(new Date(endDate), "yyyy-MM");
  const [fetchReport, { data, called, loading }] = useLazyQuery(
    fetchReportQuery
  );

  const handleReport = () => {
    fetchReport({
      variables: {
        distributorId,
        from: fromDate,
        to: endDate,
      },
    });
  };

  useEffect(() => {
    if (called && data && data.report && !loading) {
      handleDownload(
        data?.report,
        name,
        `${formattedFrom} - ${formattedTo}`,
        usage
      );
    }
  }, [called, data, formattedFrom, formattedTo, loading, name, usage]);

  return (
    <div className="AccountancyReport">
      <div className="AccountancyReport__item">{formattedFrom}</div>
      <div className="AccountancyReport__item">{formattedTo}</div>
      <div className="AccountancyReport__item">
        {usage} {t("accountancy.licenses")}
      </div>
      <div className="AccountancyReport__item">
        {accounts} {t("generic.accounts")}
      </div>
      <div className="AccountancyReport__item AccountancyReport__download-button">
        <Button
          buttonStyle="Button__primary"
          disabled={loading}
          click={() => handleReport()}
        >
          <Icon name="iconDownload" width={12} />
          <div className="AccountancyReport__download-button-text">
            {t("generic.download")}
          </div>
        </Button>
      </div>
    </div>
  );
};

const handleDownload = (inputData, distributorName, period, total) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const rawData = formatExcelData(inputData);

  const heading = [
    [distributorName, "", "", "", "", "", "", "", "", "", "", "", period],
  ];

  const ws = XLSX.utils.aoa_to_sheet(heading);
  XLSX.utils.sheet_add_json(ws, rawData, {
    origin: -1,
  });

  const totalRange = XLSX.utils.decode_range(ws["!ref"]);

  let aoa = [["Total"]];

  for (let C = totalRange.s.c + 1; C <= totalRange.e.c; ++C) {
    const cellAddress = { c: C, r: totalRange.e.r + 1 };
    const cellRef = XLSX.utils.encode_cell(cellAddress);

    const cell = {
      f: `SUM(${cellRef.substring(0, 1)}3:${cellRef.substring(0, 1)}${
        totalRange.e.r + 1
      })`,
    };

    aoa[0].push(cell);
  }

  XLSX.utils.sheet_add_aoa(ws, aoa, {
    origin: -1,
  });

  const totalCalculated = [
    ["", "", "", "", "", "", "", "", "", "", "", "", total],
  ];

  XLSX.utils.sheet_add_aoa(ws, totalCalculated, {
    origin: -1,
  });

  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const saveData = new Blob([excelBuffer], { type: fileType });
  // Get excel file
  FileSaver.saveAs(saveData, `${distributorName} - ${period}${fileExtension}`);
};

const fetchReportQuery = gql`
  query report($distributorId: String, $from: String, $to: String) {
    report(from: $from, to: $to, distributorId: $distributorId)
  }
`;
